<template>
  <form class="form" @submit.prevent="checkConditions">
    <Heading :level="3"> Defina sua senha </Heading>
    <BaseText size="p6" :color="NebraskaColors.elementSecondary">
      A senha deve ter pelo menos 8 caracteres. Evite usar datas e sequências.
    </BaseText>
    <InputPassword
      v-model="form.password"
      data-testid="password"
      class="form-input"
      label="Senha"
      :password-strength="passwordDetails.progress"
      :password-strength-color="passwordDetails.color"
      :helper-text="passwordDetails.tip"
      :status="passwordDetails.status"
      @input="debouncedVerify"
      @blur="trackFillPassword"
      @keydown.tab="focusConfirmPassword"
    />
    <InputPassword
      id="confirmpassword"
      v-model="form.confirmPassword"
      data-testid="confirmpassword"
      class="form-input"
      label="Confirmar senha"
      :helper-text="confirmPasswordDetails.tip"
      :status="confirmPasswordDetails.status"
      @blur="handleConfirmPassword"
    />
    <ButtonPrimary
      id="submit"
      data-testid="submit-button"
      class="form-button"
      label="Continuar"
      :disabled="formFulfilled"
      size="large"
      type="submit"
      :loading="isLoading || isSignInLoading"
    />
    <Recaptcha ref="recaptcha" @token="onRecaptchaToken" />
  </form>
</template>
<script setup lang="ts">
import { computed, inject, onMounted, ref, Ref } from 'vue';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import {
  BaseText,
  ButtonPrimary,
  Heading,
  InputPassword
} from '@warrenbrasil/nebraska-web';
import {
  InputStatusType,
  IPasswordValidationResponse,
  PasswordValidation,
  ProspectPasswordForm
} from '@/modules/onboarding/signup/types';
import { debounce } from 'lodash-es';
import {
  passwordVerify,
  prospectRegisterPassword
} from '@/modules/onboarding/signup/services/api';
import { notify } from '@/modules/common/helpers/notification';
import { NotificationTypes } from '@/modules/common/services/http/notification';
import {
  IRecaptcha,
  Recaptcha,
  RecaptchaActions
} from '@/modules/authentication/components/Recaptcha';
import {
  trackFillConfirmPassword,
  trackFillPassword,
  trackSendPassword,
  trackSignupPasswordView
} from '@/modules/onboarding/signup/helpers/track-events';
import { SignInForm } from '../../types';

const form = ref<ProspectPasswordForm>({
  password: '',
  confirmPassword: ''
});
const isLoading = ref<boolean>(false);
const isSignInLoading = inject('isLoading') as Ref<boolean>;

const recaptcha = ref<InstanceType<typeof Recaptcha> | null>(null);
const recaptchaToken = ref<string>('');
const passwordDetails = ref<PasswordValidation>({
  progress: 0,
  color: NebraskaColors.statusNegative,
  isValid: false,
  status: InputStatusType.Default,
  tip: ''
});
const confirmPasswordDetails = ref({
  status: InputStatusType.Default,
  tip: ''
});
const signInForm = inject('form') as Ref<SignInForm>;

const setFormPassword = inject('setFormPassword') as (password: string) => void;
const handleRecaptchaSignIn = inject('handleRecaptchaSignIn') as (
  RecaptchaAction: RecaptchaActions
) => void;

const formFulfilled = computed(() => {
  const filteredFormValues = Object.entries(form.value)
    .filter(([key]) => key !== 'token')
    .map(([, value]) => value);

  return (
    filteredFormValues.some(value => !value) || !passwordDetails.value.isValid
  );
});

function onRecaptchaToken(token: string) {
  recaptchaToken.value = token;
  recaptchaToken.value && submit();
}

function focusConfirmPassword(event: KeyboardEvent) {
  event.preventDefault();
  document.getElementById('confirmpassword')?.focus();
}

const calculatePasswordDetails = (
  passwordValidationResponse?: IPasswordValidationResponse
) => {
  if (!passwordValidationResponse)
    return {
      progress: 0,
      color: NebraskaColors.statusNegative,
      isValid: false,
      status: InputStatusType.Error,
      tip: ''
    };
  const scoreMap = {
    0: { progress: 0.25, color: NebraskaColors.statusNegative },
    1: { progress: 0.5, color: NebraskaColors.statusAlert },
    3: { progress: 0.75, color: NebraskaColors.statusPositive },
    4: { progress: 1, color: NebraskaColors.statusPositive }
  };
  const tip = passwordValidationResponse.tip;
  const { progress, color } =
    scoreMap[passwordValidationResponse.score as keyof typeof scoreMap];
  const isValid = passwordValidationResponse.score >= 3;
  let status = InputStatusType.Default;

  if (form.value.password && !isValid) {
    status = InputStatusType.Error;
  }

  return { progress, color, isValid, status, tip };
};

const debouncedVerify = debounce(async () => {
  isLoading.value = true;
  try {
    const response = await passwordVerify({ password: form.value.password });
    passwordDetails.value = calculatePasswordDetails(response);
  } catch (err) {
    passwordDetails.value = calculatePasswordDetails();
  } finally {
    isLoading.value = false;
  }
}, 1000);

function handleConfirmPassword() {
  trackFillConfirmPassword();
  if (form.value.password !== form.value.confirmPassword) {
    confirmPasswordDetails.value = {
      status: InputStatusType.Error,
      tip: 'A confirmação de senha deve ser igual a senha'
    };

    return true;
  }
  confirmPasswordDetails.value = {
    status: InputStatusType.Default,
    tip: ''
  };
  return false;
}

async function handleRecaptcha() {
  const recaptchaEl = recaptcha.value as unknown as IRecaptcha;
  isLoading.value = true;

  try {
    await recaptchaEl.onChallenge(RecaptchaActions.SIGN_UP);
  } catch {
    isLoading.value = false;
    notify({
      group: 'wrn-notifications',
      text: 'Ocorreu um erro ao continuar, verifique sua internet e tente novamente.',
      type: NotificationTypes.error
    });
  }
}

async function checkConditions() {
  const conditions = [handleConfirmPassword];

  for (const condition of conditions) {
    const shouldStopSubmit = await condition();
    if (shouldStopSubmit) {
      return;
    }
  }

  await handleRecaptcha();
}

async function submit() {
  isLoading.value = true;
  trackSendPassword();
  try {
    await prospectRegisterPassword(
      {
        email: signInForm.value.email,
        password: form.value.password
      },
      recaptchaToken.value
    );
    setFormPassword(form.value.password);
    handleRecaptchaSignIn(RecaptchaActions.SIGN_IN);
  } catch (err) {
    notify({
      group: 'wrn-notifications',
      text: 'Ocorreu um erro ao continuar, verifique sua internet e tente novamente.',
      type: NotificationTypes.error
    });
  } finally {
    isLoading.value = false;
  }
}

onMounted(() => {
  trackSignupPasswordView();
});
</script>
<style scoped lang="less">
.form {
  gap: 0 !important;

  &-input {
    width: 100%;
    margin-top: @size-spacing-x600;
  }
  &-button {
    width: 100%;
    margin-top: @size-spacing-x500;
  }
  &-alert {
    position: absolute;
    bottom: 0;
  }

  @media screen and (max-width: 768px) {
    width: 100%;
  }
}
</style>
